import { useState } from 'react';
import { Shield, Lock, Key, Mail, Smartphone, Check, X, Loader as Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';

interface SecuritySettingsProps {
  isGlobalDarkMode: boolean;
}

export const SecuritySettings: React.FC<SecuritySettingsProps> = ({ isGlobalDarkMode: _ }) => {
  const { toast } = useToast();
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [enrollmentStep, setEnrollmentStep] = useState<'choice' | 'phone' | 'verify'>('choice');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [factorId, setFactorId] = useState<string>('');

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'New passwords do not match',
        variant: 'destructive'
      });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters long',
        variant: 'destructive'
      });
      return;
    }

    setPasswordLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Password updated successfully'
      });

      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update password',
        variant: 'destructive'
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleEnableEmailMFA = async () => {
    setMfaLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user?.email) {
        throw new Error('No email found for user');
      }

      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp'
      });

      if (error) throw error;

      if (data) {
        setFactorId(data.id);
        setEnrollmentStep('verify');

        toast({
          title: 'Verification code sent',
          description: 'Please check your email for the verification code'
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to enable email MFA',
        variant: 'destructive'
      });
    } finally {
      setMfaLoading(false);
    }
  };

  const handleEnablePhoneMFA = () => {
    setEnrollmentStep('phone');
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMfaLoading(true);

    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'phone',
        phone: phoneNumber
      });

      if (error) throw error;

      if (data) {
        setFactorId(data.id);
        setEnrollmentStep('verify');

        toast({
          title: 'Verification code sent',
          description: 'Please check your phone for the verification code'
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send verification code',
        variant: 'destructive'
      });
    } finally {
      setMfaLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setMfaLoading(true);

    try {
      const { data, error } = await supabase.auth.mfa.challengeAndVerify({
        factorId: factorId,
        code: verificationCode
      });

      if (error) throw error;

      setTwoFactorEnabled(true);
      setEnrollmentStep('choice');
      setVerificationCode('');
      setPhoneNumber('');

      toast({
        title: 'Success',
        description: 'Two-factor authentication enabled successfully'
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Invalid verification code',
        variant: 'destructive'
      });
    } finally {
      setMfaLoading(false);
    }
  };

  const handleDisableMFA = async () => {
    setMfaLoading(true);

    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();

      if (factors && factors.totp.length > 0) {
        const { error } = await supabase.auth.mfa.unenroll({
          factorId: factors.totp[0].id
        });

        if (error) throw error;
      }

      if (factors && factors.phone.length > 0) {
        const { error } = await supabase.auth.mfa.unenroll({
          factorId: factors.phone[0].id
        });

        if (error) throw error;
      }

      setTwoFactorEnabled(false);
      setEnrollmentStep('choice');

      toast({
        title: 'Success',
        description: 'Two-factor authentication disabled'
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to disable MFA',
        variant: 'destructive'
      });
    } finally {
      setMfaLoading(false);
    }
  };

  const cancelEnrollment = () => {
    setEnrollmentStep('choice');
    setPhoneNumber('');
    setVerificationCode('');
    setFactorId('');
  };

  const inputClass = 'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-workspace-accent bg-background border-input text-foreground placeholder:text-muted-foreground';

  return (
    <div className="space-y-6">
      {/* Password Security Card */}
      <div className="rounded-lg border p-6 bg-card border-border">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-workspace-accent/15 text-workspace-accent">
            <Lock className="w-5 h-5" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Password Security</h3>
        </div>

        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-foreground">Current Password</label>
            <input
              type="password"
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
              placeholder="Enter current password"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 text-foreground">New Password</label>
            <input
              type="password"
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              placeholder="Enter new password"
              required
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 text-foreground">Confirm New Password</label>
            <input
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
              placeholder="Confirm new password"
              required
              className={inputClass}
            />
          </div>
          <button
            type="submit"
            disabled={passwordLoading}
            className="px-4 py-2 bg-workspace-accent hover:bg-workspace-accent-hover text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {passwordLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            {passwordLoading ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>

      {/* Two-Factor Authentication and API Keys in 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Two-Factor Authentication Card */}
        <div className="rounded-lg border p-6 bg-card border-border">
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-2 rounded-lg ${twoFactorEnabled ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'}`}>
              <Shield className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Two-Factor Authentication</h3>
            {twoFactorEnabled && (
              <span className="ml-auto flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-success/15 text-success">
                <Check className="w-3 h-3" />
                Enabled
              </span>
            )}
          </div>

          <div className="space-y-4">
            {enrollmentStep === 'choice' && !twoFactorEnabled && (
              <>
                <p className="text-sm text-muted-foreground">
                  Add an extra layer of security to your account by enabling two-factor authentication.
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleEnableEmailMFA}
                    disabled={mfaLoading}
                    className="w-full px-4 py-2 bg-success hover:bg-success/90 text-success-foreground rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Enable via Email
                  </button>
                  <button
                    onClick={handleEnablePhoneMFA}
                    disabled={mfaLoading}
                    className="w-full px-4 py-2 bg-success hover:bg-success/90 text-success-foreground rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Smartphone className="w-4 h-4" />
                    Enable via Phone
                  </button>
                </div>
              </>
            )}

            {enrollmentStep === 'phone' && (
              <form onSubmit={handlePhoneSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-foreground">Phone Number</label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+1234567890"
                    required
                    className={inputClass}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={mfaLoading}
                    className="flex-1 px-4 py-2 bg-success hover:bg-success/90 text-success-foreground rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {mfaLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {mfaLoading ? 'Sending...' : 'Send Code'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEnrollment}
                    className="px-4 py-2 rounded-lg font-medium transition-colors bg-secondary hover:bg-secondary/80 text-secondary-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </form>
            )}

            {enrollmentStep === 'verify' && (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-foreground">Verification Code</label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    required
                    maxLength={6}
                    className={inputClass}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={mfaLoading}
                    className="flex-1 px-4 py-2 bg-success hover:bg-success/90 text-success-foreground rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {mfaLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {mfaLoading ? 'Verifying...' : 'Verify Code'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEnrollment}
                    className="px-4 py-2 rounded-lg font-medium transition-colors bg-secondary hover:bg-secondary/80 text-secondary-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </form>
            )}

            {twoFactorEnabled && (
              <>
                <p className="text-sm text-muted-foreground">
                  Two-factor authentication is currently enabled for your account.
                </p>
                <button
                  onClick={handleDisableMFA}
                  disabled={mfaLoading}
                  className="w-full px-4 py-2 bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {mfaLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {mfaLoading ? 'Disabling...' : 'Disable 2FA'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* API Keys Card */}
        <div className="rounded-lg border p-6 bg-card border-border">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-info/15 text-info">
              <Key className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">API Keys</h3>
          </div>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Manage your API keys for integrations and external services.
            </p>
            <button className="px-4 py-2 bg-workspace-accent hover:bg-workspace-accent-hover text-white rounded-lg font-medium transition-colors">
              Manage API Keys
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
